import type { DocHistoryResponse, MonitoringLogRowResponse } from '../../openapi/types';

export type StateChange = {
  entry: DocHistoryResponse;
  /** Vyplněné jen tam, kde se režim právě tímto záznamem změnil. */
  previousFlow?: DocHistoryResponse['flow'];
};

export type TimelineEvent = MonitoringLogRowResponse & {
  changedState: string;
  relatedHistory: StateChange[];
};

export type TimelineItem =
  | { kind: 'event'; key: string; time: string; event: TimelineEvent }
  | { kind: 'states'; key: string; time: string; states: StateChange[] };

export type TimelineDay = {
  key: string;
  date: string;
  items: TimelineItem[];
};

export type TimelineInput = {
  rows: MonitoringLogRowResponse[];
  history: DocHistoryResponse[];
  /** Pod nejstarší načtenou událostí osa pokračuje další stránkou. */
  hasMore: boolean;
};

export const getDayKey = (isoDate: string) => new Date(isoDate).toDateString();

const byNewestEvent = (a: MonitoringLogRowResponse, b: MonitoringLogRowResponse) =>
  new Date(b.created).getTime() - new Date(a.created).getTime();

const byNewestEntry = (a: DocHistoryResponse, b: DocHistoryResponse) =>
  new Date(b.updated).getTime() - new Date(a.updated).getTime();

const getStateChanges = (history: DocHistoryResponse[]): StateChange[] =>
  history.map((entry, index) => {
    const older = history[index + 1];
    return older && entry.flow && entry.flow !== older.flow
      ? { entry, previousFlow: older.flow }
      : { entry };
  });

/*
 * Historie stavů je celá, události jen po načtenou stránku - pod nejstarší načtenou
 * událostí proto osa stavy nekreslí.
 */
const getHistoryFloor = (events: MonitoringLogRowResponse[], hasMore: boolean) => {
  if (!hasMore) return 0;
  const oldest = events[events.length - 1];
  return oldest ? new Date(oldest.created).getTime() : Infinity;
};

const pairWithEvents = (
  events: MonitoringLogRowResponse[],
  changes: StateChange[],
  hasMore: boolean,
  historyFloor: number,
) => {
  const paired = new Set<string>();

  const timelineEvents = events.map<TimelineEvent>((event, index) => {
    const older = events[index + 1];
    const eventTime = new Date(event.created).getTime();
    const lowerBound = older ? new Date(older.created).getTime() : historyFloor;

    // Jen změny ze dne události - u vzdálenější by čas pod hlavičkou cizího dne lhal.
    const relatedHistory = changes.filter(({ entry }) => {
      const entryTime = new Date(entry.updated).getTime();
      return (
        entryTime > lowerBound &&
        entryTime <= eventTime &&
        getDayKey(entry.updated) === getDayKey(event.created)
      );
    });

    relatedHistory.forEach(({ entry }) => paired.add(entry.url));

    return {
      ...event,
      // Nejstarší řádek zakládá stav jen tehdy, když pod ním už žádná stránka nezbývá.
      changedState:
        event.state && (older ? event.state !== older.state : !hasMore) ? event.state : '',
      relatedHistory,
    };
  });

  // Zbytek stavů - typicky změny novější než poslední událost - patří na osu samostatně.
  const looseChanges = changes.filter(
    ({ entry }) => !paired.has(entry.url) && new Date(entry.updated).getTime() > historyFloor,
  );

  return { timelineEvents, looseChanges };
};

const groupByDay = (changes: StateChange[]) =>
  changes.reduce<Map<string, StateChange[]>>((result, change) => {
    const key = getDayKey(change.entry.updated);
    const group = result.get(key);
    if (group) group.push(change);
    else result.set(key, [change]);
    return result;
  }, new Map());

export const buildTimelineDays = ({ rows, history, hasMore }: TimelineInput): TimelineDay[] => {
  const sortedEvents = [...rows].sort(byNewestEvent);
  const changes = getStateChanges([...history].sort(byNewestEntry));
  const historyFloor = getHistoryFloor(sortedEvents, hasMore);

  const { timelineEvents, looseChanges } = pairWithEvents(
    sortedEvents,
    changes,
    hasMore,
    historyFloor,
  );

  const items: TimelineItem[] = [
    ...timelineEvents.map<TimelineItem>((event) => ({
      kind: 'event',
      key: `event-${event.id}`,
      time: event.created,
      event,
    })),
    // Skupina stojí na ose v čase své nejnovější změny, historie je řazená od nejnovější.
    ...[...groupByDay(looseChanges)].map<TimelineItem>(([key, states]) => ({
      kind: 'states',
      key: `states-${key}`,
      time: states[0].entry.updated,
      states,
    })),
  ].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

  return items.reduce<TimelineDay[]>((result, item) => {
    const key = getDayKey(item.time);
    const lastGroup = result[result.length - 1];

    if (lastGroup?.key === key) {
      lastGroup.items.push(item);
    } else {
      result.push({ key, date: item.time, items: [item] });
    }

    return result;
  }, []);
};
